import {
    Dropdown,
    Icon,
    Icons,
    Info,
    ProgressPolygonList,
    Search,
    Status,
    StatusType,
    TableColumn,
    Toggle,
    Tooltip
} from '@silverfort/sf-components';
import { BaseComponent, ComponentTableColumn, SoftwareStatus } from './types';
import { removeTrailingTags } from '../../utils';
import { NODES_TYPES } from './EnvironmentOverviewUtils';
import { isNewerVersion } from '@silverfort-nodelibs/common/utils/common';
import { DISPLAY_NAME_SEPARATOR } from '@silverfort-nodelibs/common/utils/consts';

export const getBaseColumns = (softwareStatus: SoftwareStatus): TableColumn<ComponentTableColumn>[] => [
    {
        columnKey: 'name',
        title: 'Name',
        render: (item) => (
            <span className="flex gap-2">
                <div className="flex gap-2 items-center">
                    <Status statusType={item.connected ? StatusType.Success : StatusType.Error}></Status> {item.name}
                </div>
            </span>
        )
    },
    {
        columnKey: 'version',
        title: 'Version',
        render: (item) => (
            <span
                className={`h-6 rounded px-2 ${
                    item.version && isNewerVersion(softwareStatus.currentTowerVersion, item.version)
                        ? 'bg-sf-neutral-100 text-sf-neutral-500'
                        : 'bg-sf-primary-100 text-sf-primary-500'
                }`}>
                {item.version || 'N/A'}
            </span>
        )
    },
    {
        columnKey: 'status',
        title: 'Status',
        render: (item) => {
            switch (item.status) {
                case STATUS_FILTER_ITEMS.upgraded.value:
                    return (
                        <span className="flex gap-2 items-center">
                            <Icon icon={Icons.Check} className="text-sf-positive-500" /> Upgraded
                        </span>
                    );
                case STATUS_FILTER_ITEMS.readyForUpgrade.value:
                    return (
                        <span className="flex gap-2 items-center">
                            <Icon icon={Icons.MoreHorizontal} /> Ready for upgrade
                        </span>
                    );
                case STATUS_FILTER_ITEMS.pending.value:
                    return (
                        <span className="flex gap-2 items-center">
                            <Icon icon={Icons.MoreHorizontal} /> Pending
                        </span>
                    );
                case STATUS_FILTER_ITEMS.failed.value:
                    return (
                        <span className="flex gap-2 items-center">
                            <Icon icon={Icons.Warning} className="text-sf-neutral-500" /> Failed
                        </span>
                    );
                case STATUS_FILTER_ITEMS.inProgress.value:
                    const progress =
                        item.type === 0
                            ? softwareStatus.nodesUpgradeProgress
                            : item.type === 1
                            ? softwareStatus.pushServicesUpgradeProgress
                            : softwareStatus.dcsUpgradeProgress;

                    return (
                        <div className="flex gap-1 items-center">
                            <div className="mb-2 w-full">
                                <ProgressPolygonList
                                    isAnimationActive={false}
                                    items={[
                                        {
                                            progress: progress,
                                            endColor: '#A8CEF8',
                                            startColor: '#188DED'
                                        }
                                    ]}
                                />
                            </div>
                            <span className="text-13 text-sf-gray-800 font-sf">{progress}%</span>
                        </div>
                    );
                case STATUS_FILTER_ITEMS.notConnected.value:
                    return (
                        <div className="flex gap-2 items-center">
                            <Status statusType={StatusType.Error}></Status> Not connected
                        </div>
                    );
                default:
                    return item.status;
            }
        }
    }
];

const compareComponentsInventoryName = (inventoryName: string, component: string, componentsType: string) => {
    // since node inventory name suffix is dynamic between versions. we considering only the prefix for comparison
    if (componentsType === NODES_TYPES.NODES) {
        const nodeId = inventoryName.split(DISPLAY_NAME_SEPARATOR)[0];
        const componentId = component.split(DISPLAY_NAME_SEPARATOR)[0];
        return nodeId === componentId;
    } else {
        return component === inventoryName;
    }
};

export const calcStatus = (
    connected: boolean,
    version: string,
    inventoryName: string,
    softwareStatus: SoftwareStatus,
    componentsType: string
) => {
    if (!connected) {
        return STATUS_FILTER_ITEMS.notConnected.value;
    }

    let isVersionMatched = version === (softwareStatus.versionUpgradeInProgress || softwareStatus.maxTowerVersion);

    if (componentsType === NODES_TYPES.DCS) {
        const hasPendingTowerUpgrade =
            !!softwareStatus.maxTowerVersion &&
            isNewerVersion(softwareStatus.maxTowerVersion, softwareStatus.currentTowerVersion);
        isVersionMatched = !hasPendingTowerUpgrade && version === softwareStatus.currentCastleVersion;
    }

    const isInProgress = softwareStatus.inProgressComponents.find((comp) =>
        compareComponentsInventoryName(inventoryName, comp, componentsType)
    );

    const isFailed = softwareStatus.failedComponents.find((comp) =>
        compareComponentsInventoryName(inventoryName, comp, componentsType)
    );

    const isQueued = softwareStatus.queuedComponents.find((comp) =>
        compareComponentsInventoryName(inventoryName, comp, componentsType)
    );

    if (isVersionMatched && !isInProgress) {
        return STATUS_FILTER_ITEMS.upgraded.value;
    } else if (isInProgress) {
        return STATUS_FILTER_ITEMS.inProgress.value;
    } else if (isQueued) {
        return STATUS_FILTER_ITEMS.pending.value;
    } else if (isFailed || softwareStatus[`${componentsType}UpgradeError`]) {
        return STATUS_FILTER_ITEMS.failed.value;
    } else {
        return STATUS_FILTER_ITEMS.readyForUpgrade.value;
    }
};

export const getComponents = (selectedTab, serversData = {}): BaseComponent[] => {
    return serversData[selectedTab] || [];
};

export const buildDisplayedComponents = ({
    searchTerm,
    selectedFilters,
    sortSettings,
    components,
    softwareStatus,
    componentsType
}) => {
    let componentsToDisplay = [...components];
    if (searchTerm) {
        componentsToDisplay = componentsToDisplay.filter((comp) =>
            comp.name.toLowerCase().includes(searchTerm.toLowerCase())
        );
    }

    for (const key in selectedFilters) {
        if (key === 'status' && selectedFilters[key].length > 0) {
            componentsToDisplay = componentsToDisplay.filter((comp) =>
                selectedFilters[key].includes(
                    calcStatus(comp.connected, comp.version, comp.inventoryName, softwareStatus, componentsType)
                )
            );
            continue;
        }

        if ((key === 'az' || key === 'domains') && selectedFilters[key].length > 0) {
            componentsToDisplay = componentsToDisplay.filter((comp) => {
                const compValues = Array.isArray(comp[key]) ? comp[key] : [comp[key]];
                return selectedFilters[key].some((filter) => compValues.includes(filter));
            });

            continue;
        }

        if (selectedFilters[key].length > 0) {
            componentsToDisplay = componentsToDisplay.filter(
                (comp) =>
                    selectedFilters[key].find(
                        (filterSelection) =>
                            filterSelection === comp[key] || filterSelection?.toString() === comp[key]?.toString()
                    ) !== undefined
            );
        }
    }

    componentsToDisplay.sort((a, b) => {
        let aField;
        let bField;

        if (sortSettings.sortBy === 'az' || sortSettings.sortBy === 'domains') {
            aField = a[sortSettings.sortBy];
            bField = b[sortSettings.sortBy];

            if (Array.isArray(aField)) {
                aField = aField[0] || '';
            }

            if (Array.isArray(bField)) {
                bField = bField[0] || '';
            }
        } else if (sortSettings.sortBy === 'status') {
            aField = calcStatus(a.connected, a.version, a.inventoryName, softwareStatus, componentsType);
            bField = calcStatus(b.connected, b.version, b.inventoryName, softwareStatus, componentsType);
        } else if (sortSettings.sortBy === 'protected') {
            aField = a.is_protected;
            bField = b.is_protected;
        } else {
            aField = a[sortSettings.sortBy];
            bField = b[sortSettings.sortBy];
        }

        const aStr = aField != null ? aField.toString() : '';
        const bStr = bField != null ? bField.toString() : '';
        return aStr.localeCompare(bStr);
    });

    return sortSettings.direction === 'desc' ? componentsToDisplay.reverse() : componentsToDisplay;
};

export const STATUS_FILTER_ITEMS = {
    readyForUpgrade: { value: 'readyForUpgrade', title: 'Ready for upgrade' },
    inProgress: { value: 'inProgress', title: 'In progress' },
    upgraded: { value: 'upgraded', title: 'Upgraded' },
    failed: { value: 'failed', title: 'Failed' },
    pending: { value: 'pending', title: 'Pending' },
    notConnected: { value: 'notConnected', title: 'Not connected' }
};

export const buildComponentsOverviewFilters = ({
    components,
    setSelectedFilters,
    selectedFilters,
    selectedTab,
    softwareStatus,
    handleAutoDcUpgrade,
    setShowDcPopup,
    setSearchTerm,
    setSoftwareStatus,
    isEditor,
    searchTerm
}) => {
    const versionValues = Array.from(new Set(components.map((comp) => comp.version)));
    return (
        <div className="h-8 w-full flex justify-between items-center">
            <div className="flex gap-2">
                <Dropdown
                    removable={selectedFilters.version.length > 0}
                    onRemove={() => setSelectedFilters((prev) => ({ ...prev, version: [] }))}
                    title="Current version"
                    items={versionValues.map((version) => ({
                        value: version,
                        title: removeTrailingTags(version)
                    }))}
                    multiple
                    onChange={(values) => setSelectedFilters((prev) => ({ ...prev, version: values }))}
                    variant="filter"
                    selectedItems={selectedFilters.version}
                />
                {selectedTab === NODES_TYPES.DCS && (
                    <Dropdown
                        removable={selectedFilters.is_protected.length > 0}
                        onRemove={() => setSelectedFilters((prev) => ({ ...prev, is_protected: [] }))}
                        title="Protected"
                        items={[
                            { value: true, title: 'Protected' },
                            { value: false, title: 'Unprotected' }
                        ]}
                        multiple
                        onChange={(values) => setSelectedFilters((prev) => ({ ...prev, is_protected: values }))}
                        variant="filter"
                        selectedItems={selectedFilters.is_protected}
                    />
                )}
            </div>
            <div className="flex items=center gap-4">
                {selectedTab === NODES_TYPES.DCS && (
                    <div className="flex gap-2 items-center">
                        <Toggle
                            disabled={!isEditor}
                            className="toggle"
                            checked={softwareStatus.autoADAdapterUpgrade}
                            onChange={(checked) =>
                                checked ? handleAutoDcUpgrade(checked, setSoftwareStatus) : setShowDcPopup(true)
                            }
                        />
                        <div className="text-13 text-sf-gray-800">DC automatic upgrade</div>
                        <div>
                            <Tooltip title="When enabled, DCs will be upgraded automatically after the nodes (as long as the DC updater service is enabled in the DC). If disabled, you’ll need to upgrade the DCs manually during each upgrade process.">
                                <Info size={16} className="text-sf-gray-400" />
                            </Tooltip>
                        </div>
                    </div>
                )}
                <Search
                    border
                    text={searchTerm}
                    placeholder="Search by Name"
                    onChange={(value) => setSearchTerm(value)}
                />
            </div>
        </div>
    );
};
